/**
 * @fileoverview Partner SDK for secure communication with external partner injection code like tealium or pendo
 *
 * This SDK provides a secure interface for partner scripts to interact with the main application.
 * It includes parameter validation, sanitization, and access control to ensure security.
 *
 */

import loggerService from '@/modules/common/services/loggerService';
import communicationSdk from './communicationSdk';

/**
 * Recursively sanitizes a value to prevent XSS attacks
 *
 * @param {*} value - The value to sanitize
 * @returns {*} Sanitized value with script tags and javascript: protocols removed
 *
 * @example
 * const clean = sanitizeValue('<script>alert("xss")</script>Hello');
 * console.log(clean); // 'Hello'
 */
const sanitizeValue = (value) => {
  if (typeof value === 'string') {
    // Basic XSS prevention - remove script tags and javascript: protocols
    return value
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/javascript:/gi, '');
  }
  if (typeof value === 'object' && value !== null) {
    const sanitized = {};
    Object.keys(value).forEach((key) => {
      sanitized[key] = sanitizeValue(value[key]);
    });
    return sanitized;
  }
  return value;
};

/**
 * Sanitizes and validates parameters for the given action
 *
 * @param {string} action - The action name
 * @param {Object} params - Parameters to sanitize
 * @returns {Object} Sanitized parameters containing only allowed fields
 *
 * @example
 * const sanitized = sanitizeParams('openModal', { modalName: 'Test', malicious: '<script>' });
 * console.log(sanitized); // { modalName: 'Test' }
 */
const sanitizeParams = (action, params) => {
  // Define allowed parameters for each action
  const allowedParams = {
    GetUser: [],
    GetJWKSToken: ['appCodeName'],
    OpenModal: ['modalName', 'modalProps'],
    Navigate: ['path', 'route', 'queryParams', 'type', 'newTab'],
  };

  const allowed = allowedParams[action] || [];
  const sanitized = {};

  // Only include allowed parameters
  allowed.forEach((key) => {
    if (Object.hasOwn(params, key)) {
      sanitized[key] = sanitizeValue(params[key]);
    }
  });

  return sanitized;
};

/**
 * Logs API request for monitoring and debugging
 *
 * @param {string} action - The action that was called
 * @param {boolean} success - Whether the action succeeded
 * @param {string|null} [error=null] - Error message if action failed
 *
 * @example
 * logRequest('getUser', true);
 * logRequest('openModal', false, 'Modal not found');
 */
const logRequest = (action, success, error = null) => {
  const log = [`Partner API Call: ${action} -> ${success ? 'SUCCESS' : 'FAILED'}`, {
    error,
    timestamp: new Date().toISOString(),
  }];

  // Check if logging is enabled in production
  const isProduction = window.pov_config.VUE_APP_ENV === 'production';
  const isLoggerOn = window.businessPlatformSDK?.isLoggerOn || false;

  if (isProduction && isLoggerOn) {
    loggerService.error(...log);
  } else if (!isProduction) {
    console.log(...log);
  }
};

/**
 * Main API method for partners to call actions
 *
 * @async
 * @param {string} action - The action name to execute
 * @param {Object} [params={}] - Parameters for the action
 * @returns {Promise<Object>} Result object with success status, data, and timestamp
 *
 * @example
 * const result = await callAction('getUser', {}, {});
 * console.log(result); // { success: true, data: {...}, timestamp: 1234567890 }
 */
const callAction = async (action, params = {}) => {
  try {
    // Sanitize and validate parameters
    const sanitizedParams = sanitizeParams(action, params);

    // Call the actual communication SDK
    const result = await communicationSdk(action, sanitizedParams);

    // Log the request for monitoring
    logRequest(action, true);

    return {
      success: true,
      data: result,
      timestamp: Date.now(),
    };
  } catch (error) {
    logRequest(action, false, error.message);
    return {
      success: false,
      error: error.message,
      timestamp: Date.now(),
    };
  }
};

/**
 * Builds the global SDK interface with individual action methods
 * All methods are available to all users
 *
 * @returns {Object} SDK interface object with action methods
 *
 * @example
 * await businessPlatformSDK.getUser(); // Calls getUser action
 */
const buildSDKInterface = () => ({
  /**
   * Legacy call method for backward compatibility
   * @param {string} action - Action name
   * @param {Object} params - Action parameters
   * @returns {Promise<Object>} Action result
   */
  call: (action, params) => callAction(action, params),

  /**
   * Gets the current user information
   * @returns {Promise<Object>} User data
   * @example
   * const user = await businessPlatformSDK.getUser();
   */
  getUser: () => callAction('GetUser', {}),

  /**
   * Gets JWKS token for authentication
   * @param {string} appId - Application ID to retrieve the token for
   * @returns {Promise<Object>} JWKS token data
   * @example
   * const token = await businessPlatformSDK.getJWKSToken('my-app-id');
   */
  getJWKSToken: (appId) => callAction('GetJWKSToken', {
    appCodeName: appId,
  }),

  /**
   * Opens a modal in the main application
   *
   * @param {string} modalName - The modal name
   * @param {Object} [modalParams={}] - Modal params based on the modal requirements
   * @returns {Promise<Object>} Modal operation result
   *
   * @example
   * // Simple usage
   * await businessPlatformSDK.openModal('AppMarket', {appCodeName: 'tips'});
   */
  openModal: (modalName, modalParams = {}) => {
    const result = { modalName };
    if (modalParams && Object.keys(modalParams).length > 0) {
      result.modalProps = modalParams;
    }
    return callAction('OpenModal', result);
  },

  /**
   * Navigates to a different page or URL
   *
   * @param {string} type - Navigation type ('relative'/'absolute')
   * @param {string} [path] - Path for the redirect
   * @param {boolean} [newTab=false] - Whether to open in new tab
   * @returns {Promise<Object>} Navigation result
   *
   * @example
   * // Simple usage
   * await businessPlatformSDK.navigate('relative', '/dashboard', false);
   *
   * // Object usage
   * await businessPlatformSDK.navigate({path: '/app/inbox', type: 'relative', newTab: false});
   */
  navigate: (type, path, newTab = false) => callAction('Navigate', { type, path, newTab }),

  /**
   * Flag to control logging in production
   * Set to true to enable logging in production environment
   * @type {boolean}
   * @example
   * window.businessPlatformSDK.isLoggerOn = true;
   */
  isLoggerOn: false,
});

/**
 * Global SDK interface exposed to partner applications
 * Available for all users
 * @global
 * @namespace businessPlatformSDK
 *
 * @example
 * // Usage in partner scripts
 * const user = await window.businessPlatformSDK.getUser();
 * await window.businessPlatformSDK.openModal('AppMarket');
 *
 * // Enable logging in production
 * window.businessPlatformSDK.isLoggerOn = true;
 *
 * // Get JWKS token with app ID
 * const token = await window.businessPlatformSDK.getJWKSToken('my-app-id');
 */
window.businessPlatformSDK = buildSDKInterface();

/**
 * Main callAction function for internal use
 * @type {Function}
 */
export default callAction;
